import * as React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Collapse,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";

export default function SelectExistingQuestionGroup({
  selectGroups = [],
  createSelectedQuestionGroup,
  activeGroups = [],
}) {
  const [open, setOpen] = React.useState(false);
  const [openQuestions, setOpenQuestions] = React.useState({});
  const [selectedGroupIds, setSelectedGroupIds] = React.useState(new Set());
  const [filteredGroups, setFilteredGroups] = React.useState([]);
  const [groupTypes, setGroupTypes] = React.useState({});

  React.useEffect(() => {
    if (!Array.isArray(selectGroups)) {
      console.warn("selectGroups must be an array");
      setFilteredGroups([]);
      return;
    }

    const activeGroupIds = new Set(activeGroups.map((g) => g.id));

    // These are groups whose id is in activeGroupIds
    const filtered = selectGroups.filter((group) =>
      activeGroupIds.has(group.id)
    );
    setFilteredGroups(filtered);
  }, [selectGroups, activeGroups]);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedGroupIds(new Set());
    setGroupTypes({});
    setOpenQuestions({});
  };

  const toggleQuestionVisibility = (groupId) => {
    setOpenQuestions((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleSelectGroup = (group) => {
    setSelectedGroupIds((prev) => {
      const updated = new Set(prev);
      if (updated.has(group.id)) {
        updated.delete(group.id);
        setGroupTypes((types) => {
          const newTypes = { ...types };
          delete newTypes[group.id];
          return newTypes;
        });
      } else {
        updated.add(group.id);
        setGroupTypes((types) => ({ ...types, [group.id]: "Score" }));
      }
      return updated;
    });
  };

  const handleGroupTypeChange = (groupId, value) => {
    setGroupTypes((prev) => ({ ...prev, [groupId]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedGroupIds.size === 0) return;

    const selected = Array.from(selectedGroupIds)
      .map((groupId) => {
        const group = selectGroups.find((g) => g.id === groupId);
        if (!group) return null;
        return {
          ...group,
          group_type: groupTypes[group.id] || "Score",
          question: group.question ?? [],
        };
      })
      .filter(Boolean);

    if (selected.length > 0) {
      createSelectedQuestionGroup.mutate({ selectedGroups: selected });
    }
    handleClose();
  };

  return (
    <>
      <Button variant="outlined" onClick={handleClickOpen}>
        Select Existing Question Group
      </Button>

      {filteredGroups.length === 0 && filteredGroups.length > 0 && (
        <Stack spacing={2} sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6">Active Question Groups</Typography>
          {filteredGroups.map((group) => (
            <Card key={group.id} variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold">
                  {group.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {group.description}
                </Typography>
                <Stack>
                  {" "}
                  {group.question?.length > 0 && (
                    <Stack sx={{ mt: 1 }} spacing={1}>
                      {group.question.slice(0, 3).map((q) => (
                        <Typography key={q.id} variant="body2">
                          • {q.name}
                        </Typography>
                      ))}
                      {group.question.length > 3 && (
                        <Typography variant="body2" color="primary">
                          +{group.question.length - 3} more questions
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Select Existing QA Question Group</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Typography variant="body1" sx={{ mb: 2 }} color="GrayText">
              Select from an already‐active question groups
            </Typography>

            {filteredGroups.length === 0 && (
              <Alert severity="warning" sx={{ my: 2 }}>
                <Typography>No active question groups found.</Typography>
              </Alert>
            )}

            <Stack spacing={2} sx={{ py: 2 }}>
              {filteredGroups.map((group) => (
                <Card key={group.id}>
                  <CardContent
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                    }}
                  >
                    <Typography variant="h6">{group.name}</Typography>

                    {selectedGroupIds.has(group.id) && (
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Group Question Type</InputLabel>
                        <Select
                          value={groupTypes[group.id] || "Score"}
                          label="Group Question Type"
                          onChange={(e) =>
                            handleGroupTypeChange(group.id, e.target.value)
                          }
                        >
                          <MenuItem value="Score">Score Group</MenuItem>
                          <MenuItem value="Text Input">
                            Text Input Group
                          </MenuItem>
                          <MenuItem value="Yes/No">Yes / No Group</MenuItem>
                          <MenuItem value="Data Capture">
                            Data Capture Group
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="outlined"
                        onClick={() => toggleQuestionVisibility(group.id)}
                      >
                        {openQuestions[group.id] ? "Collapse" : "View"}
                      </Button>
                      <Button
                        variant="contained"
                        color={
                          selectedGroupIds.has(group.id)
                            ? "secondary"
                            : "primary"
                        }
                        onClick={() => handleSelectGroup(group)}
                      >
                        {selectedGroupIds.has(group.id) ? "Deselect" : "Select"}
                      </Button>
                    </Box>
                  </CardContent>

                  <Collapse in={openQuestions[group.id]}>
                    <Card color="inherit" variant="outlined">
                      <CardContent sx={{ p: 1 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          color="GrayText"
                        >
                          Questions
                        </Typography>
                      </CardContent>
                      <CardContent sx={{ p: 1 }}>
                        {group.question?.length > 0 ? (
                          <Stack spacing={1}>
                            {group.question.map((question) => (
                              <Card key={question.id}>
                                <CardContent
                                  style={{
                                    display: "flex",
                                    itemsAlign: "center",
                                  }}
                                  sx={{ gap: 1, p: 1.5, mt: 1 }}
                                >
                                  <Typography>{question.name}</Typography>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        ) : (
                          <Typography>No questions available</Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Collapse>
                </Card>
              ))}
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                disabled={selectedGroupIds.size === 0}
              >
                Submit
              </Button>
            </Stack>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
